import { hannWindow, magnitudeSpectrum } from '@/util/fft';

/**
 * Tempo detection.
 *
 * Three steps, each of which can be looked at on its own:
 *
 *  1. **An onset envelope.** The audio is reduced to one value every few
 *     milliseconds: how much *louder* this frame is than the last. Rises are
 *     kept and falls discarded, because an onset is an increase in energy and a
 *     decay is not an event. What survives is a spiky curve that peaks where
 *     things are struck.
 *  2. **Autocorrelation of that envelope.** A recording at 120 BPM has beats
 *     half a second apart, so its envelope correlates with itself at a lag of
 *     half a second, and again at every multiple. The lag with the strongest
 *     correlation, over the range of plausible tempos, is the beat period.
 *  3. **Octave folding and a fit to the clip.** Autocorrelation cannot tell 90
 *     from 180 — both are real periodicities of the same music — so the answer
 *     is folded into a musically usual range. Then, because material being
 *     warped is nearly always a loop, the estimate is nudged to whatever tempo
 *     makes the clip a whole number of beats, if one is close by. That last step
 *     is what turns "about 124" into exactly 124.
 *
 * No FFT: the envelope is broadband energy. That is weaker than spectral flux
 * on dense material, where a bass note and a hi-hat both register only as
 * "louder", but it is honest about what it does, has no windowing parameters to
 * get wrong, and is fast enough to run on the main thread for a few bars.
 */

export interface TempoEstimate {
  bpm: number;
  /**
   * How much the winning lag stood out from the rest, roughly 0 to 1. Below
   * about 0.2 the audio has no strong pulse and the number should not be
   * trusted — the caller is expected to say so rather than pretend.
   */
  confidence: number;
  /** True when the estimate was snapped to make the clip a whole number of beats. */
  fittedToLength: boolean;
}

export interface TempoDetectOptions {
  /** Tempos outside this are folded into it by halving or doubling. */
  minBpm?: number;
  maxBpm?: number;
  /** Length of the audio being analysed, if it should be fitted to a whole number of beats. */
  fitToLengthSec?: number;
  /** How onsets are measured. Spectral by default; see `onsetEnvelope`. */
  method?: OnsetMethod;
}

const DEFAULT_MIN_BPM = 70;
const DEFAULT_MAX_BPM = 180;

/** Envelope resolution. About 5ms at 48kHz — finer than any onset needs. */
const HOP = 256;

/**
 * Analysis window for the spectrum: about 21ms at 48kHz. Long enough to resolve
 * a bass note into its own bins, short enough that a drum hit is not spread over
 * two frames.
 */
const FRAME = 1024;

/**
 * Band edges, in Hz. Flux is measured inside each band and normalised there
 * before the bands are summed, which is the whole point of doing this
 * spectrally: a kick and a hi-hat contribute equally to the pulse even though
 * one is twenty decibels louder than the other.
 */
const BAND_EDGES_HZ = [0, 100, 250, 600, 1500, 4000, 12000, Number.POSITIVE_INFINITY];

/**
 * How wide each onset is smeared before the envelope is correlated. Wide enough
 * that where the hop grid falls stops mattering, narrow enough that two hits a
 * sixteenth apart at 180 BPM stay separate.
 */
const ONSET_SMOOTH_SEC = 0.012;

/**
 * The tempo prior. Centred where most music sits, and wide enough — a bit under
 * an octave of standard deviation — that genuinely slow or fast material is
 * still found rather than dragged towards the middle.
 */
const PRIOR_CENTRE_BPM = 120;
const PRIOR_WIDTH_OCTAVES = 0.7;

export type OnsetMethod = 'spectral' | 'energy';

/**
 * Onset envelope: how much *more* is happening now than a moment ago.
 *
 * Two ways of measuring that, and the difference matters:
 *
 * - `energy` reduces each frame to one number, its loudness, and takes the rise.
 *   Cheap, no windowing to get wrong, and adequate on anything sparse. On a mix
 *   it fails in a specific way: a sustained bass note holds the level up, and a
 *   hi-hat twenty decibels below it barely moves the total, so the hats — which
 *   are where the pulse actually is — become invisible.
 * - `spectral` takes the rise bin by bin and normalises within each frequency
 *   band before summing. A quiet hat is a large rise *in its own band*, so it
 *   counts for as much as the kick. This is the default, and the reason the
 *   project carries an FFT at all.
 *
 * Both are rectified: a fall in energy is a decay rather than an event, and
 * counting it would put a peak at the end of every note as well as the start.
 */
export function onsetEnvelope(
  channels: readonly Float32Array[],
  sampleRate: number,
  method: OnsetMethod = 'spectral',
  hop = HOP,
): Float32Array {
  const raw = method === 'energy' ? energyFlux(channels, hop) : spectralFlux(channels, sampleRate, hop);
  if (raw.length === 0) return raw;

  // Smoothed, and this is not cosmetic. A hit is one frame wide in the raw
  // difference, but a beat period is rarely a whole number of frames, so
  // successive hits land on alternating frames. Correlating spikes that narrow
  // then scores a *two*-beat lag perfectly and a one-beat lag at half strength,
  // and the detector confidently reports half the tempo. Widening each onset to
  // a few milliseconds removes the dependence on where the hop grid happens to
  // fall.
  return smooth(raw, Math.max(3, Math.round((ONSET_SMOOTH_SEC * sampleRate) / hop)));
}

/** Rise in broadband loudness from one hop to the next. */
function energyFlux(channels: readonly Float32Array[], hop: number): Float32Array {
  const length = channels[0]?.length ?? 0;
  const frames = Math.floor(length / hop);
  if (frames < 2) return new Float32Array(0);

  const energy = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    let sum = 0;
    const from = f * hop;
    for (const channel of channels) {
      for (let i = from; i < from + hop; i++) sum += channel[i] * channel[i];
    }
    // Loudness, not power: a decibel-like scale keeps a quiet hit visible next
    // to a loud one, which is what makes a real performance detectable.
    energy[f] = Math.log1p(Math.sqrt(sum / (hop * Math.max(1, channels.length))) * 1000);
  }

  const out = new Float32Array(frames - 1);
  for (let f = 1; f < frames; f++) out[f - 1] = Math.max(0, energy[f] - energy[f - 1]);
  return out;
}

/** Rise in magnitude per bin, gathered into bands and normalised within each. */
function spectralFlux(channels: readonly Float32Array[], sampleRate: number, hop: number): Float32Array {
  const length = channels[0]?.length ?? 0;
  const frames = Math.floor((length - FRAME) / hop) + 1;
  if (frames < 2) return energyFlux(channels, hop);

  const window = hannWindow(FRAME);
  const mono = new Float32Array(FRAME);
  const bins = FRAME >> 1;
  const binHz = sampleRate / FRAME;

  // Which band each bin belongs to, worked out once.
  const bandOf = new Int8Array(bins);
  const bandCount = BAND_EDGES_HZ.length - 1;
  for (let k = 0; k < bins; k++) {
    const hz = k * binHz;
    let band = bandCount - 1;
    for (let b = 0; b < bandCount; b++) {
      if (hz >= BAND_EDGES_HZ[b] && hz < BAND_EDGES_HZ[b + 1]) {
        band = b;
        break;
      }
    }
    bandOf[k] = band;
  }

  const bandFlux: Float32Array[] = Array.from({ length: bandCount }, () => new Float32Array(frames - 1));
  let previous: Float64Array | null = null;

  for (let f = 0; f < frames; f++) {
    const from = f * hop;
    mono.fill(0);
    for (const channel of channels) {
      for (let i = 0; i < FRAME && from + i < channel.length; i++) mono[i] += channel[from + i];
    }
    const scale = 1 / Math.max(1, channels.length);
    for (let i = 0; i < FRAME; i++) mono[i] *= scale;

    const spectrum = magnitudeSpectrum(mono, window);
    // Compressed before differencing, so a rise is judged in proportion to what
    // was already there rather than in absolute magnitude.
    for (let k = 0; k < bins; k++) spectrum[k] = Math.log1p(spectrum[k] * 100);

    if (previous) {
      for (let k = 1; k < bins; k++) {
        const rise = spectrum[k] - previous[k];
        if (rise > 0) bandFlux[bandOf[k]][f - 1] += rise;
      }
    }
    previous = spectrum;
  }

  // Each band is scaled by its own average movement before the bands are added
  // together. Without this the loudest band decides the answer on its own, which
  // is the failing this whole method exists to avoid.
  const out = new Float32Array(frames - 1);
  for (const band of bandFlux) {
    let mean = 0;
    for (const v of band) mean += v;
    mean /= Math.max(1, band.length);
    if (mean <= 1e-9) continue;
    for (let i = 0; i < band.length; i++) out[i] += band[i] / mean;
  }
  return out;
}

/** Convolution with a short Hann window, normalised so levels are unchanged. */
function smooth(values: Float32Array, width: number): Float32Array {
  if (width < 2 || values.length === 0) return values;
  const window = new Float32Array(width);
  let sum = 0;
  for (let i = 0; i < width; i++) {
    window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * (i + 1)) / (width + 1));
    sum += window[i];
  }
  const out = new Float32Array(values.length);
  const half = width >> 1;
  for (let i = 0; i < values.length; i++) {
    let acc = 0;
    for (let k = 0; k < width; k++) {
      const j = i + k - half;
      if (j >= 0 && j < values.length) acc += values[j] * window[k];
    }
    out[i] = acc / sum;
  }
  return out;
}

/** Mean-removed autocorrelation of the envelope at one lag, normalised. */
function correlationAt(envelope: Float32Array, mean: number, lag: number): number {
  const count = envelope.length - lag;
  if (count <= 1) return 0;
  let dot = 0;
  let energyA = 0;
  let energyB = 0;
  for (let i = 0; i < count; i++) {
    const a = envelope[i] - mean;
    const b = envelope[i + lag] - mean;
    dot += a * b;
    energyA += a * a;
    energyB += b * b;
  }
  const norm = Math.sqrt(energyA * energyB);
  return norm <= 1e-12 ? 0 : dot / norm;
}

/**
 * How well a lag explains the envelope: the correlation at that lag and at its
 * own multiples, weighted by a preference for ordinary tempos.
 *
 * The comb matters because autocorrelation scores every multiple of the beat
 * period, and on a performance that is not perfectly on the grid a two- or
 * three-beat lag can edge out the beat itself — errors partly cancel over the
 * longer span. The real beat period is the only lag whose *every* multiple is
 * also a peak, so it wins even when it is not individually the tallest.
 *
 * The prior is applied here rather than afterwards because folding the answer by
 * octaves at the end cannot undo a bad choice: a three-beat period halved is a
 * beat and a half, which is nothing at all.
 */
function combScore(envelope: Float32Array, mean: number, lag: number): number {
  let comb = 0;
  let terms = 0;
  for (let k = 1; k <= 4; k++) {
    const multiple = Math.round(lag * k);
    if (multiple > envelope.length / 2) break;
    // Later multiples count for less: they are measured over less overlap and
    // drift further on any real performance.
    comb += correlationAt(envelope, mean, multiple) / k;
    terms += 1 / k;
  }
  return terms === 0 ? -Infinity : comb / terms;
}

/** How likely a tempo is before hearing anything: a preference for the ordinary. */
function tempoPrior(bpm: number): number {
  const octaves = Math.log2(bpm / PRIOR_CENTRE_BPM) / PRIOR_WIDTH_OCTAVES;
  return Math.exp(-0.5 * octaves * octaves);
}

function weightedComb(
  envelope: Float32Array,
  mean: number,
  lag: number,
  framesPerSec: number,
): number {
  return combScore(envelope, mean, lag) * tempoPrior((60 * framesPerSec) / lag);
}

/** Folds a tempo into the preferred range by repeated halving or doubling. */
export function foldTempo(bpm: number, minBpm: number, maxBpm: number): number {
  let out = bpm;
  for (let i = 0; i < 8 && out < minBpm; i++) out *= 2;
  for (let i = 0; i < 8 && out > maxBpm; i++) out /= 2;
  return out;
}

/**
 * The tempo nearest `bpm` that makes `lengthSec` a whole number of beats.
 *
 * Loops are cut on bar lines, so their length is a whole number of beats almost
 * by definition. Snapping to that turns a measurement with a percent of error
 * into an exact answer — but only when the nearest such tempo is already close,
 * since forcing a non-loop onto a beat grid would invent precision that is not
 * there.
 */
export function fitTempoToLength(bpm: number, lengthSec: number, tolerance = 0.04): number | null {
  if (!(lengthSec > 0) || !(bpm > 0)) return null;
  const beats = Math.round((bpm * lengthSec) / 60);
  if (beats < 1) return null;
  const fitted = (beats * 60) / lengthSec;
  return Math.abs(fitted - bpm) / bpm <= tolerance ? fitted : null;
}

/**
 * Estimates the tempo of a recording, or returns null when there is nothing
 * periodic in it to measure.
 */
export function detectTempo(
  channels: readonly Float32Array[],
  sampleRate: number,
  options: TempoDetectOptions = {},
): TempoEstimate | null {
  const minBpm = options.minBpm ?? DEFAULT_MIN_BPM;
  const maxBpm = options.maxBpm ?? DEFAULT_MAX_BPM;

  const envelope = onsetEnvelope(channels, sampleRate, options.method);
  if (envelope.length < 32) return null;

  const framesPerSec = sampleRate / HOP;
  let mean = 0;
  for (const v of envelope) mean += v;
  mean /= envelope.length;
  if (mean <= 1e-9) return null;

  // Search a generous span of lags, then fold: the true pulse may sit at half or
  // twice the tempo anyone would name, and refusing to look there would make the
  // search miss it entirely rather than merely name it differently.
  const minLag = Math.max(2, Math.floor((60 / (maxBpm * 2)) * framesPerSec));
  const maxLag = Math.min(envelope.length - 2, Math.ceil((60 / (minBpm / 2)) * framesPerSec));
  if (maxLag <= minLag) return null;

  const scores = new Float64Array(maxLag - minLag + 1);
  let bestScore = -Infinity;
  let total = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    const score = correlationAt(envelope, mean, lag);
    scores[lag - minLag] = score;
    total += score;
    if (score > bestScore) bestScore = score;
  }
  if (bestScore <= 0) return null;

  // The shortest lag that is nearly as good as the best one. Autocorrelation
  // scores every multiple of the beat period, so the strongest peak is often two
  // or four beats; taking the earliest strong peak finds the beat itself rather
  // than the bar.
  // Choosing the single strongest lag is not enough. Autocorrelation scores every
  // multiple of the beat period, and on a performance that is not perfectly on
  // the grid a two- or three-beat lag can edge out the beat itself — the errors
  // partly cancel over the longer span. The answer then comes back as a third of
  // the tempo.
  //
  // So each candidate is scored as a comb: itself plus its own multiples. The
  // real beat period is the only lag whose every multiple is also a peak, so it
  // wins even when it is not individually the tallest. Only local maxima are
  // considered, because the shoulder of a peak is not a periodicity.
  let bestLag = 0;
  let bestComb = -Infinity;
  for (let lag = minLag + 1; lag < maxLag; lag++) {
    const i = lag - minLag;
    if (scores[i] < scores[i - 1] || scores[i] < scores[i + 1]) continue;
    if (scores[i] <= 0) continue;

    const weighted = weightedComb(envelope, mean, lag, framesPerSec);
    if (weighted > bestComb) {
      bestComb = weighted;
      bestLag = lag;
    }
  }
  if (bestLag === 0) return null;

  // Half-time is always as good a fit as the beat: every other onset lands on
  // it. The prior cannot settle that, since 87 and 174 sit about equally far
  // from the middle. So when the faster reading is nearly as strong, take it —
  // a listener counting along with a recording counts the beats, not every
  // second one. Only a real periodicity can be nearly as strong, so this cannot
  // run away into nonsense.
  //
  // The comparison here deliberately leaves the prior out. Between a period and
  // half of it, both readings explain the audio equally well, so the question is
  // not which tempo is more likely in general but whether the faster one fits
  // the recording as well — and that is what the unweighted comb measures. With
  // the prior included, a 174 BPM loop loses to 87 purely because 87 is nearer
  // the middle of the range, which is not a fact about the recording.
  let bestBare = combScore(envelope, mean, bestLag);
  for (let i = 0; i < 2; i++) {
    const half = bestLag / 2;
    if (half < minLag + 1 || half >= maxLag) break;
    // Search a couple of frames either side. Half of a lag rarely lands exactly
    // on a whole frame, and the correlation peak is sharp enough that being one
    // frame off reads as a much worse fit than it is.
    let candidate = 0;
    let candidateScore = -Infinity;
    for (let lag = Math.floor(half) - 2; lag <= Math.ceil(half) + 2; lag++) {
      if (lag < minLag + 1 || lag >= maxLag) continue;
      const bare = combScore(envelope, mean, lag);
      if (bare > candidateScore) {
        candidateScore = bare;
        candidate = lag;
      }
    }
    if (candidate === 0 || candidateScore < bestBare * 0.9) break;
    bestLag = candidate;
    bestBare = Math.max(bestBare, candidateScore);
    bestComb = weightedComb(envelope, mean, bestLag, framesPerSec);
  }

  bestScore = scores[Math.max(0, Math.min(scores.length - 1, bestLag - minLag))];
  const count = scores.length;

  // Parabolic interpolation around the peak: the true period rarely lands on a
  // whole hop, and a hop is a third of a percent at these tempos.
  const before = correlationAt(envelope, mean, bestLag - 1);
  const after = correlationAt(envelope, mean, bestLag + 1);
  const denominator = before - 2 * bestScore + after;
  const shift = denominator === 0 ? 0 : (0.5 * (before - after)) / denominator;
  const lag = bestLag + Math.max(-1, Math.min(1, shift));

  const rawBpm = (60 * framesPerSec) / lag;
  const folded = foldTempo(rawBpm, minBpm, maxBpm);

  const average = count > 0 ? total / count : 0;
  let variance = 0;
  for (let i = 0; i < scores.length; i++) variance += (scores[i] - average) ** 2;
  const deviation = Math.sqrt(variance / Math.max(1, scores.length));
  // How far the winning lag stands above the spread of all the others. A real
  // pulse towers over its neighbours; noise produces a best-of-many peak that is
  // only a few standard deviations up, however large its raw correlation looks.
  const z = deviation <= 1e-9 ? 0 : (bestScore - average) / deviation;
  const confidence = Math.max(0, Math.min(1, (z - 2) / 6));

  const fitted = options.fitToLengthSec ? fitTempoToLength(folded, options.fitToLengthSec) : null;
  return {
    bpm: fitted ?? folded,
    confidence,
    fittedToLength: fitted !== null,
  };
}
